/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { keyframes } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'

interface ScheduleCardSkeletonProps {
  /** How many placeholder rows to stack. Defaults to 5 — roughly a viewport. */
  count?: number
}

/**
 * Loading placeholder for the schedule list.
 *
 * Deliberately a *neutral* row rather than a game- or event-specific one: the
 * list mixes both kinds, so a generic block avoids implying which type is about
 * to load. Follows the conventions established by `EventDetailSkeleton` (pulse
 * animation, shared `bar` style).
 *
 * Purely decorative, so it is hidden from assistive tech; the list's own
 * loading state is what a screen reader announces.
 */
const ScheduleCardSkeleton = ({ count = 5 }: ScheduleCardSkeletonProps) => (
  <div data-testid='schedule-card-skeleton' aria-hidden='true' css={listStyle}>
    {Array.from({ length: count }).map((_, row) => (
      <div key={row} css={cardStyle}>
        {/* Logo + primary/secondary text block */}
        <div css={leadStyle}>
          <div css={[bar, logoStyle]} />
          <div css={textStackStyle}>
            <div css={[bar, titleStyle]} />
            <div css={[bar, subtitleStyle]} />
          </div>
        </div>

        {/* Meta column (time / location) */}
        <div css={metaStyle}>
          <div css={[bar, metaLineStyle]} />
          <div css={[bar, metaLineShortStyle]} />
        </div>
      </div>
    ))}
  </div>
)

// ---- styled ----

const pulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 0.9; }
  100% { opacity: 0.5; }
`

const bar = css`
  border-radius: 8px;
  background-color: ${colors.SECONDARY_300};
  animation: ${pulse} 1.4s ease-in-out infinite;
`

const listStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 0;
`

const cardStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 72px;
  padding: 16px;
  border-radius: 8px;
  background-color: ${colors.SECONDARY_400};
`

const leadStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`

const logoStyle = css`
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 4px;
`

const textStackStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const titleStyle = css`
  width: 180px;
  height: 14px;
`

const subtitleStyle = css`
  width: 120px;
  height: 12px;
`

const metaStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex: none;
`

const metaLineStyle = css`
  width: 90px;
  height: 12px;
`

const metaLineShortStyle = css`
  width: 60px;
  height: 12px;
`

export default ScheduleCardSkeleton
